import type { TaskFileUploadType } from "pages/project/common/taskBase.typing";
import type { OfferStatus } from "pages/project/offer/offer.typing";
import type { RequestStatus } from "pages/project/request/request.typing";
import type { TaskStatus } from "pages/project/task/task.typing";

export const USER_TYPES: Record<UserType, UserTypeCode> = {
  supplier: "S",
  customer: "C",
};

type UserType = "supplier" | "customer";
export type UserTypeCode = "C" | "S";

export const FileArchiveCreationStatus = {
  New: 10,
  OnGoing: 20,
  Finished: 30,
  InError: 40,
};

export interface User {
  email: string;
  firstName: string;
  lastName: string;
  userType: UserTypeCode;
  id: number;
  similarContactIds: number[];
  chatEnabled: boolean;
}

export interface ResetPasswordTokenValidity {
  isValid: boolean | undefined;
}

export interface ZipFullDownloadDtoResponse {
  estimatedFileSize: number;
  guid: string; // not used for now, could be used as a fail-safe when the hub doesn't respond after some time
  status: number;
  missingFiles?: string[] | null;
  warnings?: string[] | null;
}

export interface FileDownloadUrlDtoResponse {
  downloadUrl: string;
}

export enum TaskTypeEnum {
  Translation = 1,
  Auxiliary = 2,
  QualityControl = 4,
  All = 7, // Translation | Auxiliary | QualityControl
}

export interface PreprocessingDeliveryInfoDto {
  willTriggerCatUpdates: boolean;
  updateMode?: string;
}

export interface PreprocessingInputDataDto {
  sourceFiles: PreprocessingFileDto[];
  targetLanguages: CodeDescriptionDto[];
}

export interface DeliveryFileMappingOptionsDto {
  fileMappingSuggestions: FileMappingSuggestionDto[];
}

export interface SourceFileDto {
  fileName: string;
  managedFileId: number;
}

export interface CodeDescriptionDto {
  code: string;
  description: string;
}

export interface PreprocessingFileDto {
  id: number;
  fileName: string;
}

export interface FileMappingSuggestionDto {
  uploadedFileName: string;
  sourceFile: PreprocessingFileDto | null;
  languages: CodeDescriptionDto[];
  ruleNameApplied: string | null;
  isAutoDetected: boolean;
}

export interface OfferDtoResponse {
  offers: OfferDto[];
}

export interface ValidationRequestDtoListResponse {
  requests: ValidationExecutionRequestDto[];
}

export interface RejectionReasonDto {
  id: number;
  description: string;
}

export interface ValidationRequestRejectionReasonDto {
  id: number;
  description: string;
}

export interface TaskBaseDto {
  id: number;
  projectName: string;
  taskCode: string;
  contactEmail: string | null;
  contactName: string | null;
  sourceLanguageCode: string;
  sourceLanguage: string;
  targetLanguageCode: string;
  targetLanguage: string;
  startDate: Date | null;
  dueDate: Date | null;
  quantity: number | null;
  unit: { description: string; decimals: number } | null;
  price: number | null;
  currency: string | null;
  taskType: string;
  customerName: string;
  secondQuantity: number | null;
  secondUnit: { description: string; decimals: number } | null;
  catToolId: number;
  qaFormRequirement: QaFormRequirementEnum;
  supplier: Supplier;
  workflowExecutionType: WorkflowExecutionType;
  portalUploadMode: PortalUploadModeEnum;
  translationTaskMtEngineFormRequirement: boolean;
  feedbackSubmissionStatus: FeedbackSubmissionStatusEnum;
  feedbackFormType: FeedbackFormTypeEnum;
  shouldShowFeedbackIcon: boolean;
  chatEnabled: boolean;
  tempoChatId: number | null;
  taskTypeEnum: TaskTypeEnum;
}

export enum WorkflowExecutionType {
  None = 0,
  CAT = 1,
  Manual = 2,
}

export enum PortalUploadModeEnum {
  Default = 0,
  S3WithAntivirus = 1,
  GatewayService = 2,
}

export enum FeedbackSubmissionStatusEnum {
  NotSubmitted = 0,
  FeedbackSubmitted = 1,
  QAFeedbackSubmitted = 2,
  FeedbackSkipped = 3,
  DTPFeedbackSubmitted = 4,
}

export enum FeedbackFormTypeEnum {
  None = 0,
  MT = 1,
  QA = 2,
  STAR = 3,
  DTP = 4,
}

export enum QaFormRequirementEnum {
  None = 1,
  Mandatory = 2,
  Optional = 3,
}

export enum XtmQaReportResultType {
  SUCCESS = 0,
  ERROR = 1,
}

export interface FeedbackDto {
  taskId: number;
  taskCode: string;
  feedbackType: FeedbackFormTypeEnum;
  qaFormRequirement: QaFormRequirementEnum;
  customerName: string;
  projectName: string;
  taskType: string;
  sourceLanguage: string;
  targetLanguage: string;
  taskStatus: TaskStatus;
}

export interface TaskDtoResponse {
  acceptedTasks: TaskDto[];
}

export interface OfferDto extends TaskBaseDto {
  taskId: number;
  status: OfferStatus;
  useTep: boolean;
  relatedTaskId: number;
}

export interface ValidationExecutionRequestDto extends TaskBaseDto {
  taskId: number;
  status: RequestStatus;
  acceptedByAnother: boolean;
}

export interface TaskDto extends TaskBaseDto {
  status: TaskStatus;
}

export interface TaskBaseFileDtoResponse {
  deliveredFiles: TaskBaseFileDto[];
}

export interface MemSourceProjectLinksDtoResponse {
  memsourceProjectLinks: MemSourceProjectLinkDto[];
}

export interface Translate5ProjectLinksDtoResponse {
  catToolProjectLinks: CatToolProjectLinkDto[];
}

export interface TaskBaseFileDto {
  fileName: string;
  creationTime: Date;
  fileType: TaskFileUploadType;
}

export interface MemSourceProjectLinkDto {
  catProjectId: string;
  catProjectLink: string;
  catProjectName: string;
}

export interface CatToolProjectLinkDto {
  projectId: string;
  projectLink: string;
  projectName: string;
}

export interface XtmProjectsDtoResponse {
  xtmProjects: XtmProjectDto[];
}

export interface XtmProjectDto {
  xtmProjectId: number;
  xtmProjectName: string;
  anonymousJobsLink: string;
}

export interface XtmJobsLinkDto {
  xtmProjectId: number;
  url: string;
}

export type CatStepsProgressDto = {
  fileName: string;
  segmentsDone: number;
  totalSegments: number;
};
export type CatTotalStepsProgressDto = {
  segmentsDone: number;
  totalSegments: number;
};

export interface CatStepsDto {
  stepsProgress: CatStepsProgressDto[];
  totalStepsProgress: CatTotalStepsProgressDto;
}

export interface QAFormCriteriaDto {
  title: string | null;
  criteria: Array<{ id: number; description: string; details: string }>;
}

export interface QAFormDto {
  answers: Array<{ key: number; value: string }>;
  criteriaGroups: Array<QAFormCriteriaDto>;
}

export interface IdName<T extends string | number = number> {
  id: T | null;
  name: string;
}

export interface Supplier extends IdName {
  supplierContact: IdName;
  supplierInternallyManaged: boolean;
  tasksVisibleForAllContacts: boolean;
}

export interface PreInvoiceDto {
  id: number;
  documentNumber: number;
  currencyCode: string;
  issuedDate: Date;
  reIssuedDate: Date | null;
  entityName: string;
  entityId: number;
  branchName: string;
  branchId: number;
  price: number;
}

export interface PreInvoiceTaskDto extends TaskDto {
  supplierPreInvoiceId: number;
  endTime: Date | null;
}

export interface PreInvoiceDetailDto {
  invoice: PreInvoiceDto;
  tasks: PreInvoiceTaskDto[];
}

export type TaskInstructionSource = "Module" | "Project" | "DocumentSet" | "Task";

export type TaskInstructionCriticality = "Low" | "Normal" | "High";

export interface TaskInstructionDto {
  instructionId: number;
  description: string;
  title: string;
  formattedText?: string | null;
  source: TaskInstructionSource;
  isGeneric: boolean;
  criticality: TaskInstructionCriticality;
}

export interface TaskInstructionListDto {
  instructions: TaskInstructionDto[];
  instructionsAcknowledgedAt: string | null;
}

export interface MemsourceTaskJobsCompletion {
  taskId: number;
  completed: boolean;
}

export interface QAReport {
  qaReportName: string;
  qaReportUrl: string;
}

export interface QAReportList {
  qaReports: QAReport[];
}
